import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from "./style.scss";
import { DownloadType } from "models/AiResearch";
import { OutlineButton } from "components/Common/Button/Outline";
import cn from "classnames";

interface Props {
  onClickPulldownItem: (type: DownloadType) => void;
  disabled: boolean;
  threshold?: number;
}

export const DownloadButton: React.FC<Props> = ({
  onClickPulldownItem,
  disabled,
  threshold
}) => {
  const [showDownloadPulldown, setShowDownloadPulldown] = useState(false);
  const [pulldownDirection, setPulldownDirection] = useState<"down" | "up">(
    "down"
  );

  const downloadPulldownRef = useRef<HTMLSpanElement>(null);

  const updatePulldownDirection = useCallback(() => {
    if (!downloadPulldownRef.current || !threshold) return;

    const { bottom } = downloadPulldownRef.current.getBoundingClientRect();

    setPulldownDirection(
      bottom > window.innerHeight - threshold ? "up" : "down"
    );
  }, [threshold]);

  useEffect(() => {
    const isNode = (target: EventTarget | null): target is Node =>
      Boolean(target && (target as Node).nodeType);

    const handleClickOutside = (event: MouseEvent) => {
      const pulldownNode = downloadPulldownRef.current as {
        contains: (node: Node | null) => boolean;
      } | null;

      if (
        pulldownNode &&
        isNode(event.target) &&
        !pulldownNode.contains(event.target)
      ) {
        setShowDownloadPulldown(false);
      }
    };

    window.addEventListener("click", handleClickOutside);

    return () => {
      window.removeEventListener("click", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!showDownloadPulldown) return;

    updatePulldownDirection();

    window.addEventListener("scroll", updatePulldownDirection);
    window.addEventListener("resize", updatePulldownDirection);

    return () => {
      window.removeEventListener("scroll", updatePulldownDirection);
      window.removeEventListener("resize", updatePulldownDirection);
    };
  }, [showDownloadPulldown, updatePulldownDirection]);

  return (
    <span ref={downloadPulldownRef} className={styles.downloadPulldown}>
      <OutlineButton
        className={styles.button}
        onClick={() => {
          if (!showDownloadPulldown) {
            updatePulldownDirection();
          }
          setShowDownloadPulldown(!showDownloadPulldown);
        }}
        disabled={disabled}
      >
        <span className={styles.downloadImage} />
        ダウンロード
      </OutlineButton>

      {showDownloadPulldown && (
        <ul
          className={cn(styles.pulldownMenu, {
            [styles.upward]: pulldownDirection === "up"
          })}
        >
          <li onClick={() => onClickPulldownItem("md")}>Markdown(.md)</li>
          <li onClick={() => onClickPulldownItem("pdf")}>PDF文書(.pdf)</li>
          <li onClick={() => onClickPulldownItem("docx")}>
            Microsoft Word(.docx)
          </li>
        </ul>
      )}
    </span>
  );
};
